import {
  createDateTimeInUserTimeZoneFromClockTime,
  formatInUserTimeZone,
  getUserCalendarDateKey,
  toUserTimeZoneClockDate,
} from "@utils/timeZone";

type TimeEntryDateSource = {
  date?: unknown;
  start?: unknown;
  dt?: unknown;
};

export const getTimeEntryDateKey = (value: unknown): string | null =>
  getUserCalendarDateKey(value);

/**
 * Grid entries already expose `date` in the selected user's timezone. Prefer
 * it over the UTC-backed `dt`, whose ISO calendar day can differ for delegated
 * users such as Benjamin.
 */
export const getTimeEntrySourceDateKey = (
  entry: TimeEntryDateSource | null | undefined,
): string | null => {
  const gridDate = getTimeEntryDateKey(entry?.date);
  if (gridDate) return gridDate;

  const instant = entry?.start ?? entry?.dt;
  if (!instant) return null;

  try {
    return formatInUserTimeZone(
      instant as Parameters<typeof formatInUserTimeZone>[0],
      "YYYY-MM-DD",
    );
  } catch {
    return null;
  }
};

export const mergeTimeEntryDateAndClockTime = (
  datePart: unknown,
  clockTime: unknown,
): Date | null => {
  if (!datePart || !clockTime) return null;

  const result = createDateTimeInUserTimeZoneFromClockTime(datePart, clockTime);
  return result instanceof Date && !Number.isNaN(result.getTime())
    ? result
    : null;
};

export const moveTimeEntryDateTimeToDate = (
  datePart: unknown,
  dateTime: unknown,
): Date | null => {
  if (!datePart || !dateTime) return null;

  const clockTime = toUserTimeZoneClockDate(dateTime);
  return clockTime ? mergeTimeEntryDateAndClockTime(datePart, clockTime) : null;
};

export const getTimeEntryClockPickerValue = (
  dateTime: unknown,
): Date | null =>
  dateTime ? toUserTimeZoneClockDate(dateTime) : null;

export const getDatePickerCalendarDateKey = (value: unknown): string | null => {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, "0");
    const day = String(value.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  return getTimeEntryDateKey(value);
};

export const getDatePickerValueFromTimeEntryDate = (
  value: unknown,
): Date | null => {
  const dateKey = getTimeEntryDateKey(value);
  if (!dateKey) return null;

  const match = dateKey.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;

  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
};
